import { getMember, isMemberSignedIn, loadMember } from '@vista-digital/occ-domain'
import TagManager from 'react-gtm-module'
import { type AnalyticsPayload, AnalyticsService } from '@/shared/services/analytics.service'
import { FilmTrailerCloseDataLayer } from '@/shared/services/gtm/models/film-trailer-close'
import { FilmTrailerDataLayer } from '@/shared/services/gtm/models/film-trailer-open'
import { FilmDetailsViewDataLayer } from '@/shared/services/gtm/models/filmdetails.gtm'
import { FilterApplyDataLayer } from '@/shared/services/gtm/models/filter.gtm'
import type { DataLayer } from '@/shared/services/gtm/models/models-gtm'
import { PageViewDataLayer } from '@/shared/services/gtm/models/page-view'
import { SearchResultsViewDataLayer } from '@/shared/services/gtm/models/search.gtm'

class GtmService extends AnalyticsService {
    public constructor() {
        super()
        this.setLogger('GtmService')
    }

    public initializeGoogleTags(gtmId: string) {
        if (this.isInitialized) {
            this.logger.warn('GTM already initialized')
            return
        }

        const tagManagerArgs = {
            gtmId: gtmId,
        }

        if (gtmId && gtmId.length > 0) {
            TagManager.initialize(tagManagerArgs)
            this.setInitialized(true)
        }
    }

    public sendDataLayer = async (dataLayerObj: DataLayer) => {
        // Tracking data for testing purposes
        this.logger.debug('Sending data layer', dataLayerObj)
        if (isMemberSignedIn()) {
            await loadMember()
            dataLayerObj.user_id = getMember()?.personalDetails.externalId
        } else {
            dataLayerObj.user_id = undefined
        }
        TagManager.dataLayer({ dataLayer: dataLayerObj })
    }

    public send(payloads: AnalyticsPayload[]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- disabling unsafe argument of type any
        payloads.forEach(this.routePayloadToDataLayer.bind(this))
    }

    private routePayloadToDataLayer(payload: AnalyticsPayload) {
        switch (payload.event) {
            case 'filter_apply':
                this.sendFilterApplyDataLayer(payload)
                break
            case 'movie_detail_page':
                this.sendFilmDetailPageDataLayer(payload)
                break
            case 'trailer_open':
                this.sendTrailerOpenDataLayer(payload)
                break
            case 'trailer_close':
                this.sendTrailerCloseDataLayer(payload)
                break
            case 'view_search_results':
                this.sendSearchResultsDataLayer(payload)
                break
            case 'page_view':
                this.sendPageViewDataLayer(payload)
                break
            default:
                this.logger.warn('No data layer mapping found for event', payload.event)
        }
    }

    private sendPageViewDataLayer(payload: AnalyticsPayload) {
        void this.sendDataLayer(
            new PageViewDataLayer({
                pageName: payload.params.page_name as string,
            })
        )
    }

    private sendFilterApplyDataLayer(payload: AnalyticsPayload) {
        const params = payload.params

        void this.sendDataLayer(
            new FilterApplyDataLayer({
                ...params,
                ...params,
            })
        )
    }
    private sendFilmDetailPageDataLayer(payload: AnalyticsPayload) {
        void this.sendDataLayer(
            new FilmDetailsViewDataLayer({
                movie_id: payload.params.film_id as string,
                filmName: payload.params.film_name as string,
                genre: ((payload.params.genre as string[]) || []).join(', '),
                technology: ((payload.params.technology as string[]) || []).join(', '),
                cinema: ((payload.params.cinema as string[]) || []).join(', '),
            })
        )
    }

    private sendTrailerOpenDataLayer(payload: AnalyticsPayload) {
        void this.sendDataLayer(
            new FilmTrailerDataLayer({
                film_id: payload.params.movie_id as string,
                film_name: payload.params.movie_title as string,
            })
        )
    }

    private sendTrailerCloseDataLayer(payload: AnalyticsPayload) {
        void this.sendDataLayer(
            new FilmTrailerCloseDataLayer({
                film_id: payload.params.movie_id as string,
                film_name: payload.params.movie_title as string,
            })
        )
    }

    private sendSearchResultsDataLayer(payload: AnalyticsPayload) {
        void this.sendDataLayer(
            new SearchResultsViewDataLayer({
                searchTerm: payload.params.search_term as string,
            })
        )
    }
}

export const gtm = new GtmService()
